import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { TooltipDirective } from '../../../../../shared/components/ui/tooltip/tooltip.directive';
import {
  FaDuotoneIconComponent,
  FaIconComponent,
  AnimationProp,
} from '@fortawesome/angular-fontawesome';
import { faGear } from '@fortawesome/sharp-duotone-regular-svg-icons';
import { faEllipsis } from '@fortawesome/pro-duotone-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe } from '@ngx-translate/core';
import { User } from '../../../../../state/user.repository';
import { UserService } from '../../../../services/user.service';
import { UserAvatar } from '../../../user-avatar/user-avatar';
import { IconBtn } from '../../../../../shared/components/ui/buttons/icon-btn/icon-btn';
import { ParticipantMiniProfile } from '../../../participant-mini-profile/participant-mini-profile';
import { SettingsProfile } from '../../../../../features/settings';
import { ContextMenu } from '../../../../../shared/components/ui/context-menu/context-menu';
import { ContextMenuTriggerDirective } from '../../../../../shared/components/ui/context-menu/context-menu-trigger.directive';
import { UserStatusBtn } from '../../../../../shared/components/user/user-status-btn/user-status-btn';
import { UserSettingsStore } from '../../../../../state/user-settings.repository';

@Component({
  selector: 'andor-user-entry',
  standalone: true,
  imports: [
    CommonModule,
    TooltipDirective,
    UserAvatar,
    IconBtn,
    ParticipantMiniProfile,
    SettingsProfile,
    ContextMenu,
    ContextMenuTriggerDirective,
    UserStatusBtn,
    FaDuotoneIconComponent,
    FaIconComponent,
    TranslatePipe,
  ],
  templateUrl: './user-entry.html',
  styleUrl: './user-entry.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserEntry implements OnInit {
  @Input() user!: User;
  @Input() showTooltip!: boolean;

  userSettingsStore = inject(UserSettingsStore);
  private userService = inject(UserService);

  iconAnimation = signal<AnimationProp | undefined>(undefined);

  // Single signal subscription per row replaces two `| async` reads in the
  // template — virtual-scroll churn was creating + tearing down two async-pipe
  // subscriptions to the same observable on every row mount.
  expanded = toSignal(this.userSettingsStore.getSidePanelClass$(), {
    initialValue: '' as '' | 'expanded',
  });

  ngOnInit(): void {}

  onStatusToggle() {
    if (this.user.isSelf) {
      this.userService.toggleStatus();
    }
  }

  // The avatar and the options button open the participant menu; a click
  // anywhere else on a participant's tile toggles them as a favorite.
  onTileClick(event: MouseEvent) {
    if ((event.target as Element).closest('.user-avatar, .actions')) return;
    this.userService.updateFavorite(this.user.id);
  }

  onMenuOpened() {
    this.iconAnimation.set(undefined);
    requestAnimationFrame(() => {
      this.iconAnimation.set(this.user.isSelf ? 'spin-pulse' : 'shake');
    });
  }

  // Clear on close so a future re-render of the icon (e.g. when the side
  // panel collapses and re-expands) does not replay the stale animation.
  onMenuClosed() {
    this.iconAnimation.set(undefined);
  }

  protected readonly faGear = faGear;
  protected readonly faEllipsis = faEllipsis;
  protected readonly faStar = faStar;
}
